/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { EdsIconsEnum, LogoMultiCardFragment } from "@cosmos/hygraph-schema";

import {
	CosmosCard,
	CosmosRichText,
	CosmosText,
	CosmosTextVariantEnum,
	EdsIcon,
	EdsIconType,
} from "@components/elements";
import { css, cx } from "@panda/css";
import { HStack, Stack } from "@panda/jsx";
import { ColorToken } from "@panda/tokens";

import { splitTrailingSegment } from "./LogoCard.utils";

type LogoCardProps = LogoMultiCardFragment & { descriptionColor?: ColorToken };

const trailingStyles = css({
	whiteSpace: "nowrap",
	display: "inline-flex",
	flexDirection: "row",
	alignItems: "center",
	"& svg": {
		display: "inline",
		marginInline: "eds.dimension.8",
	},
});

export function LogoCard({
	icon,
	title,
	desc,
	href,
	descriptionColor,
	iconGradient,
}: LogoCardProps): React.JSX.Element {
	const { leading, trailing } = splitTrailingSegment(title?.text);

	return (
		<CosmosCard variant="material" className={cx("logo-card")}>
			<Stack alignItems="start" direction="column" gap="eds.dimension.24">
				<EdsIcon icon={icon as EdsIconType} size="xl" gradient={iconGradient} />
				<Stack justify="start" gap="eds.dimension.8">
					{title?.text ? (
						<CosmosText variant={CosmosTextVariantEnum.headingMD} color="eds.text.contrast" as="h3">
							{href ? (
								<HStack gap="eds.dimension.4" alignItems="center">
									<CosmosCard.Link href={href}>
										{leading}
										<span className={trailingStyles}>
											{trailing}
											<EdsIcon size="xs" icon={EdsIconsEnum.ChevronRight} color="secondary" />
										</span>
									</CosmosCard.Link>
								</HStack>
							) : (
								title.text
							)}
						</CosmosText>
					) : null}
					{desc ? (
						<CosmosRichText
							variant={CosmosTextVariantEnum.paragraphSM}
							color={descriptionColor || "eds.text.secondary"}
							html={desc}
						/>
					) : null}
				</Stack>
			</Stack>
		</CosmosCard>
	);
}
